import { OfferDropdownItemMode } from '@/offer/new-offer-configurator/offer-dropdown-item/offer-dropdown-item.component';
import {
	Accommodation,
	AccommodationWithCount,
	ActivePriceSettingOptions,
	Description,
	DescriptionTemplate,
	DescriptionTemplateHeaders,
	DescriptionTemplateHeadersSource,
	DescriptionUse,
	FilterValues,
	FilterValuesByRoom,
	Location,
	MatProduct,
	OfferDescription,
	Organization,
	ORGANIZATION_BENEFITS_HEADER_NAME,
	Participants,
	PriceOption,
	PromoMethod,
	RoomType,
	RoomTypeWithCount,
	SellTerm,
	SellTransport,
	SellTransportWithStatistics,
	StopStatistics,
	TermDateRange,
} from './current-offer.types';
import { formatOskarDate } from '@/_utils/datetime/format-oskar-date';
import { adultsOrChildrenSufix } from '@/_utils/oskar/adults-or-children-sufix';
import { Promotion, PromotionOfferDateRange, TransportKind } from '@/offer/offer.model';
import { Params } from '@angular/router';
import sanitize from 'sanitize-html';
import { youthCampSufix } from '@/_utils/oskar/youth-camp-sufix';
import { DatePipe } from '@angular/common';

export const roomTypesForActiveParticipantsCondition = (
	a: Accommodation,
	activeFilter: FilterValues,
	isSurcharge: boolean,
): boolean => {
	return (
		(isSurcharge &&
			a.adults >= activeFilter?.participants?.adults &&
			a.children === activeFilter?.participants?.children) ||
		(isSurcharge && a.adults >= activeFilter?.participants?.adults + activeFilter?.participants?.children) ||
		(!isSurcharge &&
			a.adults === activeFilter?.participants?.adults &&
			a.children === activeFilter?.participants?.children) ||
		(!isSurcharge && a.adults + a.children === activeFilter?.participants?.adults)
	);
};

export const dropdownItemOption = (
	option: OfferDropdownItemMode,
	filterValues: FilterValues,
	isLoggedIn = false,
	roomTypesWithCount: RoomTypeWithCount[],
	statisticsStops: StopStatistics[],
	promotions?: Promotion[],
	isYouthCamp?: boolean,
	allRoomTypesAmount?: { [key: string]: number },
): string => {
	switch (option) {
		case OfferDropdownItemMode.MAT_TERM:
			if (!filterValues?.dateRange) {
				return null;
			}
			const { lower, upper, transportFrom, transportTo } = filterValues.dateRange;
			const from = formatOskarDate(new Date(transportFrom ?? lower));
			const to = formatOskarDate(new Date(transportTo ?? upper));
			const isFrom = transportFrom || lower;
			const isTo = transportTo || upper;
			return isFrom && isTo ? `${from} - ${to}` : null;
		case OfferDropdownItemMode.BOARDS:
			if (!filterValues?.board) {
				return null;
			}
			const { name: boardName } = filterValues.board;
			return !!boardName ? boardName : null;
		case OfferDropdownItemMode.DEPARTURE:
			if (filterValues?.transport?.ownTransport) {
				return 'Dojazd własny';
			}
			const stopWithStatistic = convertToSellTransportWithStatistics(
				[filterValues?.transport?.stop],
				statisticsStops,
			)[0];

			return !!stopWithStatistic.location
				? isLoggedIn && stopWithStatistic.countSlotsOk !== null
					? `${stopWithStatistic.location} (${stopWithStatistic.countSlotsOk}/${stopWithStatistic.countSlotsOp})`
					: stopWithStatistic.location
				: null;
		case OfferDropdownItemMode.PARTICIPANTS:
			if (!filterValues?.participants) {
				return null;
			}

			if (isYouthCamp) {
				return youthCampSufix(filterValues?.participants?.adults);
			}

			const text = adultsOrChildrenSufix(filterValues.participants);
			return !!text ? text : null;
		case OfferDropdownItemMode.ROOM_TYPES:
			if (!filterValues?.roomType) {
				return null;
			}

			const roomTypeWithCount = convertToAccommodationWithCount([filterValues.roomType], roomTypesWithCount)[0];
			const roomName = roomTypeWithCount.name;
			return !!roomName
				? isLoggedIn
					? `${roomName} (${roomTypeWithCount.count}/${roomTypeWithCount.available - (allRoomTypesAmount[roomName] ?? 0)}/${roomTypeWithCount.confirmedOffersCount}/${roomTypeWithCount.optionOffersCount})`
					: roomName
				: null;
		case OfferDropdownItemMode.PROMOTIONS:
			if (!filterValues?.promotion) {
				return null;
			}
			const availablePromotionsForOffer = findAvailablePromotionsForOffer(promotions, filterValues.dateRange);
			if (!availablePromotionsForOffer?.length) {
				return null;
			}
			const filterPromotionAvailable = findFilterPromotionAvailableForOffer(
				availablePromotionsForOffer,
				filterValues.promotion.id,
			);
			const setPromotion = filterPromotionAvailable
				? filterPromotionAvailable
				: availablePromotionsForOffer.length > 1
					? findBestPromo(availablePromotionsForOffer)
					: availablePromotionsForOffer[0];
			const { name: promotionName } = setPromotion.catalog;
			const { method, methodValue } = setPromotion;
			const sufix = method === PromoMethod.PERCENTAGE ? ` ${methodValue / 10}%` : ` -${methodValue / 100}zł`;
			return !!promotionName ? promotionName + sufix : null;
		case OfferDropdownItemMode.EMAIL:
		case OfferDropdownItemMode.PHONE:
		case OfferDropdownItemMode.FIRSTNAME:
		case OfferDropdownItemMode.LASTNAME:
			return;
		default:
			const value: never = option;
			throw new Error(`There is no option like ${value}`);
	}
};

/**
 * That calculates price decreased by promotion value (if promo exists). If promo doesn't exist, then it just returns price.
 * @param {number} price
 * @param {FilterValues} filterValue
 * @param {Promotion[]} promotions optional
 * @param {TermDateRange} dateRange optional
 * @return {number} calculated price decreased by promotion value (if promo exists)
 */
export const calcPromo = (
	price: number,
	filterValue: FilterValues,
	promotions?: Promotion[],
	dateRange?: TermDateRange,
): number => {
	if (!filterValue.promotion) {
		return price;
	}
	const selectedPromotions: Promotion[] = dateRange ? promotions : [filterValue.promotion];
	const selectedDateRange: TermDateRange = dateRange ?? filterValue.dateRange;
	const availablePromotionsForOffer = findAvailablePromotionsForOffer(selectedPromotions, selectedDateRange);

	if (!availablePromotionsForOffer?.length) {
		return price;
	}

	const filterPromotionAvailable = findFilterPromotionAvailableForOffer(
		availablePromotionsForOffer,
		filterValue.promotion.id,
	);

	if (filterPromotionAvailable) {
		return getDiscount(filterPromotionAvailable, price);
	}

	const bestPromotionForOffer = findBestPromo(availablePromotionsForOffer);
	return bestPromotionForOffer ? getDiscount(bestPromotionForOffer, price) : price;
};

export const calcChildrenDiscount = (price: number, childrenDiscount: ActivePriceSettingOptions): number =>
	childrenDiscount
		? childrenDiscount.priceOption.method === PriceOption.PERCENTAGE && childrenDiscount.methodValue > 0
			? price * (1 - childrenDiscount.methodValue / 1000)
			: price - childrenDiscount.methodValue
		: price;

export const calcTransportPrice = (filterValue: FilterValues, ownTransport: ActivePriceSettingOptions): number =>
	filterValue?.transport
		? filterValue.transport.ownTransport
			? (ownTransport?.methodValue ?? 0)
			: (filterValue.transport.stop?.totalFee ?? 0)
		: 0;

export const findPromo = (promotions: Promotion[], queryParams: Params): Promotion => {
	const promoBasedOnParam = queryParams['promotion']
		? promotions?.find((promo) => promo.id === +queryParams['promotion'])
		: null;

	if (promoBasedOnParam) {
		return promoBasedOnParam;
	}

	const bestPercentagePromo = promotions?.reduce((acc, promo) => {
		if (promo.method === PromoMethod.PERCENTAGE && (acc === null || acc.methodValue < promo.methodValue)) {
			acc = promo;
		}
		return acc;
	}, null);

	if (bestPercentagePromo) {
		return bestPercentagePromo;
	}

	const bestValuePromo = promotions?.reduce((acc, promo) => {
		if (promo.method === PromoMethod.VALUE && (acc === null || acc.methodValue < promo.methodValue)) {
			acc = promo;
		}
		return acc;
	}, null);

	return bestValuePromo;
};

export const findBestTerm = (sellTerms: SellTerm[]): SellTerm => {
	let bestTerm = sellTerms[0];
	for (let i = 1; i < sellTerms.length; i++) {
		if (bestTerm.termPrice > sellTerms[i].termPrice) {
			bestTerm = sellTerms[i];
		}
	}
	return bestTerm;
};

// **
// * First of all we are using termId cause it solves some errors, and indicates specific term.
// * But old configurator, and some components are using data_range param, so I left it.
// * If there isn't termId, then we're searching the term by date_range.
// * When offer-service removed we can try to remove that date_range from urls.
// **
export const findParamsTerm = (sellTerms: SellTerm[], queryParams: Params): SellTerm => {
	const termParam = queryParams['date_range'];
	const termId = queryParams['term_id'];
	let foundTerm = null;

	if (!termId && !termParam) {
		return null;
	}

	if (termId) {
		foundTerm = sellTerms.find((term) => term.id === Number(termId));
	}

	if (foundTerm) {
		return foundTerm;
	}

	const [lower, upper] = Array.isArray(termParam) ? termParam[0] : !!termParam ? termParam.split(',') : '';
	foundTerm = sellTerms.find(
		(term) =>
			(new DatePipe('pl-PL').transform(term.transportFrom, 'yyyy-MM-dd') === lower &&
				new DatePipe('pl-PL').transform(term.transportTo, 'yyyy-MM-dd') === upper) ||
			(term.dateRange.lower === lower && term.dateRange.upper === upper),
	);

	if (!foundTerm) {
		return null;
	}

	return foundTerm;
};

export const adultsParticipantsWhenSurchargeService = (roomTypes: Accommodation[]): number[] => {
	const maxParticipants = roomTypes.reduce((acc, roomType) => {
		if (roomType.adults > acc) {
			acc = roomType.adults;
		}

		return acc;
	}, 0);

	const arr = [];

	for (let i = 1; i <= maxParticipants; i++) {
		arr.push(i);
	}

	return arr;
};

export const calcRoomPrice = (
	adultPrice: number,
	childPrice: number,
	surcharge: number,
	filterValue: FilterValues,
): number => {
	if (!filterValue || !filterValue.participants || !filterValue.dateRange) {
		return null;
	}

	const { adults, children } = filterValue.participants;

	return adults * adultPrice + children * childPrice + surcharge;
};

export const findBestRoom = (accommodation: Accommodation[], participants: Participants): Accommodation => {
	// perfect room is when number of participants is the same, and the name fits to `Pokój ${participants.adults} osobowy`
	let room = accommodation?.find(
		(room) =>
			room.adults === participants.adults &&
			room.children === participants.children &&
			room.name === `Pokój ${participants.adults} osobowy`,
	);

	// we're looking for best match
	if (!room) {
		room = accommodation?.find(
			(room) => room.adults === participants.adults && room.children === participants.children,
		);
	}
	// if best match not found we're looking for a room with surcharge for adults but with a place for child
	if (!room) {
		room = accommodation?.find(
			(room) => room.adults >= participants.adults && room.children === participants.children,
		);
	}
	// if secondary choice not found then we're looking for a room where we treat child as an adult
	if (!room) {
		room = accommodation?.find((room) => room.adults >= participants.adults + participants.children);
	}

	// if no room is found and there are accommodations available, it returns the first accommodation from the list
	if (!room && accommodation?.length) {
		room = accommodation[0];
	}

	return room;
};

export const findDescriptionForTemplate = (
	product: MatProduct,
	template: DescriptionTemplateHeaders,
	descriptions: Description[],
	use: DescriptionUse,
): Description => {
	const idToMatch = template.commonDescriptionHeaders.id;
	return findDescription(
		template.source === DescriptionTemplateHeadersSource.PRODUCT ? product.descriptions : descriptions,
		idToMatch,
		use,
	);
};

export const findDescription = (descriptions: Description[], id: number, use: DescriptionUse): Description | null =>
	descriptions?.find((v) => v.descriptionHeader.id === id && (v.use === use || v.use === DescriptionUse.All)) || null;

export const addStyleToLists = (htmlString: string): string => {
	return htmlString
		?.replace('<ul>', '<ul class="list-disc list-inside p-revert">')
		?.replace('<ol>', '<ol class="!list-decimal list-outside p-revert">');
};

export const mapDescriptionData = (
	templates: DescriptionTemplate[],
	descriptions: Description[],
	activeProduct: MatProduct,
	use: TransportKind,
): OfferDescription[] => {
	const templateForActiveProduct = templates?.find((v) => v.productKind === activeProduct?.productKind.id);

	if (!templateForActiveProduct) return [];

	return templateForActiveProduct.descriptionTemplateHeaders
		.map((v) => {
			const description = findDescriptionForTemplate(
				activeProduct,
				v,
				descriptions,
				use as unknown as DescriptionUse,
			);
			const sanitizeConfig: sanitize.IOptions = {
				allowedAttributes: {
					p: ['style'],
					span: ['style'],
				},
			};

			if (!description) return null;
			return {
				...v,
				description: {
					...description,
					description: addStyleToLists(sanitize(description?.description, sanitizeConfig)),
				},
			};
		})
		.filter((v) => !!v)
		.sort((a, b) => a.order - b.order);
};

export const mapOrganizationToDescriptionData = (organization: Organization, order: number): OfferDescription => {
	if (!organization) return;

	const descriptionParts = [
		`<p>${organization.name}</p>`,
		organization.nip ? `<p>nip: ${organization.nip}</p>` : '',
		organization.regon ? `<p>regon: ${organization.regon}</p>` : '',
		organization.travelOrganizationGovernmentApproval
			? `<p>${organization.travelOrganizationGovernmentApproval}</p>`
			: '',
	];

	const organizationDescription = descriptionParts.join('');

	return {
		commonDescriptionHeaders: {
			id: null,
			comment: null,
			header: null,
			multimedia: null,
			name: ORGANIZATION_BENEFITS_HEADER_NAME,
		},
		description: {
			description: organizationDescription,
			descriptionHeader: null,
			use: DescriptionUse.All,
		},
		id: 9999,
		order: order,
		source: DescriptionTemplateHeadersSource.PRODUCT,
	};
};

/**
 * It maps the insurance description to the object which is readable by offer-benefits.component
 * @param {MatProduct} product
 * @return {OfferDescription} Object which contains description of the default insurance
 */
export const mapInsurerDataToBenefit = (product: MatProduct): OfferDescription => {
	return {
		id: 9999,
		description: {
			description: product.defaultInsurance.description,
			descriptionHeader: null,
			use: DescriptionUse.All,
		},
		order: 9999,
		source: DescriptionTemplateHeadersSource.GENERAL,
		commonDescriptionHeaders: {
			id: 9999,
			comment: '',
			header: '',
			multimedia: {
				id: '',
				name: 'insurance.svg',
				url: '/assets/insurance.svg',
			},
			name: 'Ubezpieczenie',
		},
	};
};

export const calcBoardPrice = (
	boardPrice: number,
	configuratorNights: number,
	dateRange?: FilterValues['dateRange'],
): number => {
	if (!dateRange) {
		return boardPrice;
	}

	if (boardPrice === 0) {
		return 0;
	}

	const targetNights = dateRange.nights;
	const boardPricePerNight = boardPrice / configuratorNights;

	return boardPricePerNight * targetNights;
};

export const extractLocationIds = (obj: Location): number[] => {
	let ids = [obj.id];
	if (obj.parentLocation) {
		ids = ids.concat(extractLocationIds(obj.parentLocation));
	}
	return ids;
};

export const filterPromotionsByDateRange = (promotions: Promotion[], dateRange: TermDateRange): Promotion[] => {
	return promotions?.filter((promotion) => {
		return promotion?.promotionOffers?.length
			? promotion?.promotionOffers?.some((obj) => {
					return obj && dateRange ? checkIfPromotionIsAvailableForOffer(dateRange, obj.offer) : false;
				})
			: promotion;
	});
};

export const checkIfPromotionIsAvailableForOffer = (
	offerDateRange: TermDateRange,
	promotionDateRange: PromotionOfferDateRange,
): boolean => {
	const upperDateRange = new Date(promotionDateRange.upper);
	const lowerDateRange = new Date(promotionDateRange.lower);
	const departureTime = new Date(new DatePipe('pl-PL').transform(offerDateRange.transportFrom, 'yyyy-MM-dd'));

	if (isNaN(upperDateRange as unknown as number) || isNaN(lowerDateRange as unknown as number)) {
		throw new Error('You passed an invalid date');
	}

	return lowerDateRange <= departureTime && upperDateRange >= departureTime;
};

export const findBestPromo = (promotions: Promotion[]): Promotion => {
	return promotions?.reduce((acc, promo) => {
		if (promo.method === PromoMethod.PERCENTAGE && (acc === null || acc.methodValue < promo.methodValue)) {
			acc = promo;
		}
		return acc;
	}, null);
};

export const findAvailablePromotionsForOffer = (promotions: Promotion[], dateRange: TermDateRange): Promotion[] =>
	promotions?.filter((promotion) => {
		return promotion?.promotionOffers?.length
			? promotion.promotionOffers.some((obj) => {
					return obj && dateRange ? checkIfPromotionIsAvailableForOffer(dateRange, obj.offer) : false;
				})
			: promotion;
	});

export const findFilterPromotionAvailableForOffer = (
	promotions: Promotion[],
	filterValuePromotionId: number,
): Promotion => promotions.find((promotion) => promotion?.id === filterValuePromotionId);

export const getDiscount = (promotion: Promotion, price: number): number => {
	return promotion.method === PromoMethod.PERCENTAGE && promotion.methodValue > 0
		? price * (1 - promotion.methodValue / 1000)
		: price - promotion.methodValue;
};

/**
 * It calculates extra room fee. In most cases it is 0.
 * It is more than 0 when a single person is in the room that could be sold as an double.
 * The single person needs to pay an extra fee, so we're not losing money.
 * @param {FilterValues} filterValue
 * @return {number} calculated price
 */
export const getRoomTypeFee = (filterValue: FilterValues): number =>
	filterValue.roomType
		? (filterValue.dateRange.nights * filterValue.roomType.fee) / filterValue.participants.adults
		: 0;

/**
 * That method calculates the price considering all conditions.
 * This calculates the ending price - NOT catalog price.
 * @param {boolean} isAdult
 * @param {number} termPrice
 * @param {number} boardPrice
 * @param {number} transportPrice
 * @param {Promotion[]} promotions
 * @param {TermDateRange} dateRange
 * @param {FilterValues} filterValue
 * @param {ActivePriceSettingOptions} childrenDiscount optional, only validate for children
 * @return {number} calculated price
 */
export const calcPrice = (
	isAdult: boolean,
	termPrice: number,
	boardPrice: number,
	transportPrice: number,
	promotions: Promotion[],
	dateRange: TermDateRange,
	filterValue: FilterValues,
	childrenDiscount?: ActivePriceSettingOptions,
): number => {
	let promotablePrice = termPrice + boardPrice;
	let unPromotablePrice = 0;

	if (isAdult) {
		promotablePrice += getRoomTypeFee(filterValue);
	}

	if (filterValue.transport?.ownTransport) {
		promotablePrice += transportPrice;
	} else {
		unPromotablePrice += transportPrice;
	}

	return (
		unPromotablePrice +
		(!!filterValue?.promotion?.id || isAdult
			? calcPromo(promotablePrice, filterValue, promotions, dateRange)
			: calcChildrenDiscount(termPrice + boardPrice, childrenDiscount))
	);
};

export const convertToAccommodationWithCount = (
	accommodation: Accommodation[],
	roomTypesWithCount: RoomTypeWithCount[],
): AccommodationWithCount[] =>
	accommodation?.map((roomType) => {
		const foundCount = roomTypesWithCount?.find(({ id }) => id === roomType?.id);
		return {
			...roomType,
			available: foundCount?.available ?? null,
			count: foundCount?.count ?? null,
			confirmedOffersCount: foundCount?.confirmedOffersCount ?? null,
			optionOffersCount: foundCount?.optionOffersCount ?? null,
		};
	});

export const convertToSellTransportWithStatistics = (
	sellTransport: SellTransport[],
	statisticsStops: StopStatistics[],
): SellTransportWithStatistics[] =>
	sellTransport?.map((stop) => {
		const foundStatistics = statisticsStops?.find(({ id }) => id === stop?.id);
		return {
			...stop,
			countSlotsOk: foundStatistics?.countSlotsOk ?? null,
			countSlotsOp: foundStatistics?.countSlotsOp ?? null,
		};
	});

/**
 * It calculates obj which contains data how many specific room types we have
 * @param {FilterValuesByRoom} filterValues object with each filterValue for each room
 * @param {string} roomTypeName OPTIONAL, if set then user wants amount of specific room, otherwise it will return an object with amounts of every room
 * @return {{ [key: string]: number } | number} object which contains how many specific rooms we have divided by roomTypeName | amount of how many chosen rooms we have for specific roomTypeName
 */
export const roomTypesAmount = (
	filterValues: FilterValuesByRoom,
	editedRoom: string,
	roomTypeName?: RoomType['name'],
): { [key: string]: number } | number => {
	const obj = Object.values(filterValues).reduce((acc: { [key: string]: number }, value) => {
		if (filterValues[editedRoom]?.dateRange?.termId !== value?.dateRange?.termId) {
			return acc;
		}
		if (!value.roomType) {
			return acc;
		}
		acc[value.roomType.name] = acc[value.roomType.name] ? acc[value.roomType.name] + 1 : 1;
		return acc;
	}, {});

	if (!roomTypeName) {
		return obj;
	}

	if (!obj[roomTypeName]) {
		return 0;
	}

	return obj[roomTypeName];
};
